import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Train, MapPin, Gauge, Timer, ArrowLeft, Bell, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /trip/:shareId — public read-only view of a shared PNR journey.
 * NO authentication. Family members open this to see the train move live.
 * PNR is masked so nothing sensitive leaks. Refreshes every 15s.
 */
export default function TripShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  useSeo({ title: "Track a shared journey · find2near",
           description: "Follow a train's live position, next station and ETA — shared securely by a family member." });

  const load = async () => {
    try { const r = await api.get(`/trains/share/${shareId}`); setData(r.data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Share link unavailable"); }
  };
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [shareId]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-6">
        <div className="rounded-3xl bg-white/5 border border-white/10 p-6 text-center max-w-sm" data-testid="trip-share-error">
          <Shield size={36} className="mx-auto text-rose-400 mb-2"/>
          <div className="text-lg font-black">Link unavailable</div>
          <div className="text-[12px] text-white/60 mt-1">{err}</div>
          <Link to="/live-trains" className="mt-4 inline-block rounded-full bg-emerald-500 text-slate-900 font-black text-[11px] px-4 py-1.5">Open Live Trains</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-slate-950 text-white grid place-items-center"><Loader2 className="animate-spin"/></div>;

  const s = data.live;
  const p = data.platform;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-black text-white" data-testid="trip-share-page">
      <div className="bg-gradient-to-br from-sky-700 to-indigo-900 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Link to="/live-trains" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Live trains</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Train size={12}/> Family view · PNR {data.pnr_masked}</div>
          <h1 className="heading text-2xl md:text-4xl font-black mt-1" data-testid="trip-train-name">{data.train_name}</h1>
          <p className="text-sm text-white/80 mt-1">Shared by <span className="font-black text-emerald-300">{data.owner_name}</span> · {data.from_station.name} → {data.to_station.name}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {p && (
          <div className="rounded-2xl bg-amber-500/10 border border-amber-400/30 p-3" data-testid="trip-platform">
            <div className="text-[10px] uppercase font-black text-amber-300 flex items-center gap-1"><Bell size={11}/> Boarding platform</div>
            <div className="text-sm font-black text-amber-100 mt-0.5">Platform {p.platform} · Halt {p.halt_min} min{p.coach?.walk_zone && <> · Zone {p.coach.walk_zone}</>}</div>
            {p.coach?.hint && <div className="text-[11px] text-amber-200/80 mt-0.5">{p.coach.hint}</div>}
          </div>
        )}

        <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase font-black text-white/50">Assigned</div>
          <div className="font-black text-sm">Coach {data.coach_no} · Seat {data.seat}</div>
        </div>

        {s ? (
          <div className="bg-gradient-to-br from-indigo-800 to-slate-900 rounded-3xl border border-white/10 p-4" data-testid="trip-status-card">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase font-black text-sky-300">{s.mode_label} · {s.train_no}</div>
                <div className="font-black text-lg">{s.name}</div>
              </div>
              <div className={`rounded-full text-[10px] uppercase font-black px-2 py-1 ${s.status === "running" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                {s.status === "running" ? "Running" : "At station"}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Stat icon={<Gauge size={14} className="text-emerald-400"/>} label="Speed" value={`${s.speed_kmph} km/h`}/>
              <Stat icon={<Timer size={14} className="text-amber-400"/>} label="ETA next" value={s.eta_next_min != null ? `${Math.round(s.eta_next_min)}m` : "—"}/>
              <Stat icon={<MapPin size={14} className="text-sky-400"/>} label="Position" value={`${(s.position_km || 0).toFixed(0)} km`}/>
            </div>
            <div className="mt-3 text-xs flex items-center gap-2">
              <span className="font-black" data-testid="trip-current">{s.current_station?.name || "—"}</span>
              <span className="text-white/40">→</span>
              <span className="font-black text-amber-300" data-testid="trip-next">{s.next_station?.name || "Terminal"}</span>
            </div>
            {s.distance_to_next_km != null && <div className="text-[10px] text-white/50 mt-1">{s.distance_to_next_km.toFixed(1)} km to next stop</div>}
          </div>
        ) : (
          <div className="rounded-2xl bg-white/5 p-4 text-center text-white/60 text-sm">Live status unavailable right now — refreshing…</div>
        )}

        <div className="text-[10px] text-white/40 text-center">Auto-refreshes every 15 seconds · Link expires {new Date(data.expires_at).toLocaleString()}</div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white/5 rounded-2xl p-2 text-center">
      <div className="grid place-items-center">{icon}</div>
      <div className="text-[10px] uppercase text-white/50 font-black mt-1">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}
